/**
 * moduleRegistry —— 桌面端「模块」的**唯一登记处**（一切皆插件的落地层）。
 *
 * ## 它是什么
 *
 * 一个模块（插件）= 一份描述符：id / 组 / 入口（路由或动作）/ 图标 / 名键 / 权限镜像 /
 * 阶段（ga/beta/vertical/hidden）。坞轨、坞抽屉、命令面板（Ctrl+K）、标题栏、启动台
 * **全部从这里派生**，任何消费面不许再自登记模块清单。
 *
 * 加一个模块的成本 = 这里加一条描述符 + 五语各一行 `module.<id>.name/desc`。
 * （此前是 7–8 处各自维护、零对账 —— `/dev-workbench` 曾全仓无入口、`/beehive`
 * 只能靠 Ctrl+K 到达，都是那个形态的产物。）
 *
 * ## 三条硬约束（判据见 `config/__tests__/moduleRegistry.spec.ts`）
 *
 * 1. **死入口在注册时即红**：`entry.kind === 'route'` 的 `to` 必须能解析到
 *    `router.ts` 静态路由（含 alias）或 `ManifestRouterManager.TOPOLOGY_REGISTRY`
 *    幽灵路由 —— 而不是点了落到 NotFound 才知道。
 * 2. **access 是 viewAccessRegistry 的镜像**：执法单源仍是路由 meta + useRouteGuard，
 *    本表只做对账（三方一致），不造第二个执法点。
 * 3. **五语必有 name**：`module.<id>.name` 在五种语言包里都要有非占位译文
 *    （`nav.beehive` zh="Beehive" 那种未译形态在注册时就红）。
 *
 * ## 渐进式披露（spec DOCK_PLUGIN_IA_AND_NAMING_SPEC_2026-09-29 §6）
 *
 * 轨（组 icon）→ hover 铭 → 抽屉**分节**（每节 ≤6 项、每组 ≤4 节）→ 钉住（收藏进
 * 「常用」组）→ 全集层（Ctrl+K / 启动台，含 vertical/hidden）。`stage` 是披露元数据：
 * `hidden`/`vertical` 不上轨、不进默认面板分组，但全集层永远搜得到 —— **任何被收进
 * 深层的东西必须仍可达全集层**。
 *
 * ## 命名（§4 命名法典）
 *
 * 一模块一名一键：`module.<id>.name`（主名，页头 1:1 同键）、`module.<id>.desc`
 * （一句话述，铭的「述」段与面板副行复用）、`module.<id>.tagline`（副题，诗意/品牌
 * 词降级到这里，如「全息导演协议」）。虚构前缀词（神枢/深渊/星环/蜂群/零信/量子/
 * 天候/幽灵/天九/创世/演化/泛型/图谱/中枢）整体退役，但作为**搜索别名**保留在
 * `keywords` 里 —— 老用户的肌肉记忆靠它过渡一个版本。
 */
import type { DirectorDomain } from './viewAccessRegistry';

// ── 类型 ────────────────────────────────────────────────────────────────────

export type ModuleStage = 'ga' | 'beta' | 'vertical' | 'hidden';

/** 常驻五组 + 特殊组（pinned=收藏聚合；vertical=行业垂直；tools=系统与个人）。 */
export type GroupId =
    | 'converse' | 'create' | 'operate' | 'data' | 'govern'
    | 'pinned' | 'vertical' | 'tools';

/** 入口形态：目的地（路由）或快捷动作。两者不许混排 —— 抽屉里分节呈现。 */
export type ModuleEntry =
    | { kind: 'route'; to: string }
    | { kind: 'action'; action: string };

/** viewAccessRegistry 的镜像语义（对账用，不执法）。 */
export interface ModuleAccess {
    requires?: DirectorDomain[];
    requiresOp?: boolean;
    requiresRole?: string;
    planned?: boolean;
}

export interface ModuleDescriptor {
    /** canonical id，kebab/camel，全仓唯一；i18n 键与路由判据都从它派生。 */
    id: string;
    group: GroupId;
    /** 组内节 id（如 govern 的 observe/control/security/dev）；无节组省略。 */
    section?: string;
    entry: ModuleEntry;
    /** lucide 图标名（字符串）——组件解析集中在 `config/dockIcons.ts`。 */
    icon: string;
    /** 搜索别名：旧名（含退役前缀词）与常见缩写，只进面板搜索，不进任何标签。 */
    keywords?: string[];
    stage?: ModuleStage;
    access?: ModuleAccess;
}

export interface GroupSectionDescriptor {
    id: string;
    /** 节标签键；省略 = 单节组不渲染节头（如 data 组）。 */
    labelKey?: string;
}

export interface GroupDescriptor {
    id: GroupId;
    icon: string;
    order: number;
    labelKey: string;
    descKey: string;
    /** pinned 用金色主题（收藏聚合的既有视觉方言）。 */
    theme?: 'gold';
    sections: GroupSectionDescriptor[];
}

// ── 组 ──────────────────────────────────────────────────────────────────────

const gk = (id: GroupId) => `dock.group.${id}.name` as const;
const gd = (id: GroupId) => `dock.group.${id}.desc` as const;
const sk = (key: string) => `dock.section.${key}` as const;

export const MODULE_GROUPS: readonly GroupDescriptor[] = [
    {
        id: 'pinned', icon: 'Pin', order: 0, theme: 'gold',
        labelKey: gk('pinned'), descKey: gd('pinned'),
        // 「常用」是聚合组：children 由消费方从收藏+热门动态合成（OmniDock pinnedGroup），
        // 本表不静态注册成员（判据：moduleRegistry.spec ①）。
        sections: [{ id: 'items' }],
    },
    {
        id: 'converse', icon: 'MessageSquare', order: 1,
        labelKey: gk('converse'), descKey: gd('converse'),
        sections: [
            { id: 'pages' },
            { id: 'quick', labelKey: sk('quick') },
        ],
    },
    {
        id: 'create', icon: 'Palette', order: 2,
        labelKey: gk('create'), descKey: gd('create'),
        sections: [
            { id: 'canvas', labelKey: sk('canvas') },
            // 「造物」与「画布」是两种不同的动作：画布产出内容件（图文/视频），
            // 造物产出可制造的工程件（有尺寸、公差、检查报告）。混在一节里会让
            // 用户按"创作"的预期去找"制造"的东西。`canvas` 已有 5 项（法典上限 6），
            // 再塞一个正好顶格，所以新开一节而不是挤进去。
            { id: 'fabricate', labelKey: sk('fabricate') },
            { id: 'auto', labelKey: sk('auto') },
        ],
    },
    {
        id: 'operate', icon: 'TrendingUp', order: 3,
        labelKey: gk('operate'), descKey: gd('operate'),
        sections: [
            { id: 'ops', labelKey: sk('ops') },
            { id: 'channel', labelKey: sk('channel') },
        ],
    },
    {
        id: 'data', icon: 'Database', order: 4,
        labelKey: gk('data'), descKey: gd('data'),
        sections: [{ id: 'items' }],
    },
    {
        id: 'govern', icon: 'Shield', order: 5,
        labelKey: gk('govern'), descKey: gd('govern'),
        sections: [
            { id: 'observe', labelKey: sk('observe') },
            { id: 'control', labelKey: sk('control') },
            { id: 'security', labelKey: sk('security') },
            { id: 'dev', labelKey: sk('dev') },
        ],
    },
    // —— 全集层组（不上轨；面板/启动台可达）——
    {
        id: 'vertical', icon: 'HeartPulse', order: 90,
        labelKey: gk('vertical'), descKey: gd('vertical'),
        sections: [{ id: 'items' }],
    },
    {
        id: 'tools', icon: 'Settings', order: 91,
        labelKey: gk('tools'), descKey: gd('tools'),
        // [Q3 裁决 2026-09-29] 全幅工作台/独立窗口形态的子面注册为 hidden：
        // 不上轨不进默认面板视图，但 Ctrl+K 搜得到（全集层保底可达）。
        sections: [
            { id: 'system', labelKey: sk('system') },
            { id: 'holo', labelKey: sk('holo') },
        ],
    },
];

// ── 模块（注册顺序 = 抽屉/面板内的默认顺序；节内按本表顺序）────────────────

export const MODULES: readonly ModuleDescriptor[] = [
    // ═══ 会话 converse ═══
    {
        id: 'chat', group: 'converse', section: 'pages',
        entry: { kind: 'route', to: '/pro-nexus?panel=chat' }, icon: 'MessageSquare',
        keywords: ['神枢', '会话', 'chat', 'nexus'],
    },
    {
        id: 'arena', group: 'converse', section: 'pages',
        entry: { kind: 'route', to: '/pro-nexus?panel=compare' }, icon: 'Swords',
        keywords: ['竞技场', 'compare', 'arena', '神枢'],
    },
    {
        id: 'square', group: 'converse', section: 'pages',
        entry: { kind: 'route', to: '/square' }, icon: 'Users',
        keywords: ['广场', '社区', 'square', 'global square'],
    },
    {
        id: 'bot', group: 'converse', section: 'pages',
        entry: { kind: 'route', to: '/bot' }, icon: 'Bot',
        keywords: ['bot', '机器人', '智能体'],
    },
    // 快捷动作：不是模块（无路由、不可收藏、不上标题栏），只在抽屉「快捷」节出现。
    {
        id: 'copilot', group: 'converse', section: 'quick',
        entry: { kind: 'action', action: 'copilot' }, icon: 'Sparkles',
        keywords: ['副驾', 'copilot', 'assistant'],
    },
    {
        id: 'social_pane', group: 'converse', section: 'quick',
        entry: { kind: 'action', action: 'social_pane' }, icon: 'Users',
        keywords: ['社交', '侧栏', 'social'],
    },

    // ═══ 创作 create ═══
    {
        id: 'factory', group: 'create', section: 'canvas',
        entry: { kind: 'route', to: '/factory' }, icon: 'Factory',
        keywords: ['创世', '内容工厂', '全息导演协议', 'factory'],
    },
    {
        id: 'lingxi', group: 'create', section: 'canvas',
        entry: { kind: 'route', to: '/factory?mode=lingxi' }, icon: 'Clapperboard',
        keywords: ['灵犀', 'lingxi', '乾坤策'],
    },
    {
        id: 'studio', group: 'create', section: 'canvas',
        entry: { kind: 'route', to: '/studio' }, icon: 'Film',
        keywords: ['NLE', '时序剪辑', '剪辑', 'omni studio'],
    },
    {
        id: 'a2ui', group: 'create', section: 'canvas',
        entry: { kind: 'route', to: '/a2ui' }, icon: 'LayoutTemplate',
        keywords: ['泛型', 'UI 生成', '创世沙盒', 'a2ui'],
    },
    {
        id: 'tiangong', group: 'create', section: 'fabricate',
        entry: { kind: 'route', to: '/tiangong' }, icon: 'Ruler',
        keywords: ['天工', 'CAD', '建模', '文生3D', '零件', 'engineering', 'tiangong'],
    },
    {
        id: 'world', group: 'create', section: 'canvas',
        entry: { kind: 'route', to: '/genesis-world' }, icon: 'Globe',
        keywords: ['创世', '虚拟世界', '虚拟场景', 'genesis'],
    },
    {
        id: 'workflow', group: 'create', section: 'auto',
        entry: { kind: 'route', to: '/morphic' }, icon: 'Orbit',
        keywords: ['演化', '工作流', '形变工坊', 'morphic'],
    },
    {
        id: 'rpa', group: 'create', section: 'auto',
        entry: { kind: 'route', to: '/rpa-studio' }, icon: 'Workflow',
        keywords: ['RPA', '编排', '自动化'],
    },
    {
        id: 'rpa_browser', group: 'create', section: 'auto',
        entry: { kind: 'route', to: '/browser' }, icon: 'AppWindow',
        keywords: ['RPA', '浏览器', 'browser'], access: { requires: ['workflow'] },
    },
    {
        id: 'darwin', group: 'create', section: 'auto',
        entry: { kind: 'route', to: '/darwin' }, icon: 'FlaskConical',
        keywords: ['演化', '竞技场', 'darwin', '进化'],
    },

    // ═══ 经营 operate ═══
    {
        id: 'overview', group: 'operate', section: 'ops',
        entry: { kind: 'route', to: '/matrix' }, icon: 'LayoutGrid',
        keywords: ['商业大盘', '神机矩阵', '中枢', 'matrix', 'dashboard'],
    },
    {
        id: 'growth', group: 'operate', section: 'ops',
        entry: { kind: 'route', to: '/growth-hub' }, icon: 'Zap',
        keywords: ['获客', '增长', 'growth'],
    },
    {
        id: 'selection', group: 'operate', section: 'ops',
        entry: { kind: 'route', to: '/selection' }, icon: 'Target',
        keywords: ['选品', 'OmniSelection', 'selection'],
    },
    {
        id: 'commerce', group: 'operate', section: 'ops',
        entry: { kind: 'route', to: '/commerce-ops' }, icon: 'ShoppingBag',
        keywords: ['电商', '运营', '电商控制中心', '矩阵', 'commerce'],
        access: { requiresOp: true },
    },
    {
        id: 'earn', group: 'operate', section: 'channel',
        entry: { kind: 'route', to: '/earn' }, icon: 'Coins',
        keywords: ['赚钱', '任务', '接单', '推广', '邀请', 'earn', 'income', 'task'],
        stage: 'beta',
    },
    {
        id: 'store', group: 'operate', section: 'channel',
        entry: { kind: 'route', to: '/store' }, icon: 'Store',
        keywords: ['商店', '生态', '生态商店', '天九', '天杻', 'tianshu', 'store'],
    },
    {
        id: 'beehive', group: 'operate', section: 'channel',
        entry: { kind: 'route', to: '/beehive' }, icon: 'Hexagon',
        keywords: ['蜂巢', '蜂群协作', '账号矩阵', 'beehive'],
        access: { requires: ['workflow'], planned: true },
    },
    {
        id: 'agency', group: 'operate', section: 'channel',
        entry: { kind: 'route', to: '/agency' }, icon: 'Building2',
        keywords: ['代理商', '分中心', '门户', 'agency'],
    },
    {
        id: 'delivery', group: 'operate', section: 'channel',
        entry: { kind: 'route', to: '/delivery-studio' }, icon: 'Package',
        keywords: ['企业交付', '交付', 'delivery'],
    },

    // ═══ 数据 data ═══
    {
        id: 'data', group: 'data', section: 'items',
        entry: { kind: 'route', to: '/data-studio' }, icon: 'Table2',
        keywords: ['数据工坊', '全域数据', 'data studio'],
        access: { requiresOp: true },
    },
    {
        id: 'models', group: 'data', section: 'items',
        entry: { kind: 'route', to: '/starport' }, icon: 'Boxes',
        keywords: ['模型仓库', '模型港', 'starport'],
        access: { requiresOp: true, planned: true },
    },
    {
        id: 'storage', group: 'data', section: 'items',
        entry: { kind: 'route', to: '/storage' }, icon: 'HardDrive',
        keywords: ['存储', 'storage'],
        access: { requiresOp: true, planned: true },
    },
    {
        id: 'archive', group: 'data', section: 'items',
        entry: { kind: 'route', to: '/eternity' }, icon: 'Archive',
        keywords: ['档案', '数字永生', '长期存档', 'eternity'],
    },
    {
        id: 'payloads', group: 'data', section: 'items',
        entry: { kind: 'route', to: '/payloads' }, icon: 'FileJson',
        keywords: ['负载', 'payloads'],
        access: { requires: ['architect'] },
    },

    // ═══ 治理 govern ═══
    {
        id: 'topology', group: 'govern', section: 'observe',
        entry: { kind: 'route', to: '/topology' }, icon: 'Map',
        keywords: ['拓扑', '网络', '星环', 'topology'],
        access: { requires: ['topology'] },
    },
    {
        id: 'director', group: 'govern', section: 'observe',
        entry: { kind: 'route', to: '/director' }, icon: 'Camera',
        keywords: ['导演', '态势', '天候', 'director'],
        access: { requires: ['admin'] },
    },
    {
        id: 'monitor', group: 'govern', section: 'observe',
        entry: { kind: 'route', to: '/world-monitor' }, icon: 'Radar',
        keywords: ['监控', '全域视界', '深渊', 'monitor'],
        access: { requires: ['auditor'] },
    },
    {
        id: 'telemetry', group: 'govern', section: 'observe',
        entry: { kind: 'route', to: '/cerebral' }, icon: 'Brain',
        keywords: ['遥测', '脑桥', 'cerebral'],
        access: { requires: ['auditor'] },
    },
    {
        // [本机历史 2026-10-12] 本机样本文件（CPU / 租约消耗的采样序列）读数面。
        // 无 access 镜像：读的是**本机自己的**采样文件（不含跨节点/账户级事实），
        // 与 /storage 同档。access 三方对账（moduleRegistry.spec ③）要求
        // registry ↔ viewAccessRegistry ↔ router meta 逐条一致 —— 所以三者都不登记域门。
        id: 'local_history', group: 'govern', section: 'observe',
        entry: { kind: 'route', to: '/local-history' }, icon: 'LineChart',
        keywords: ['本机历史', '采样', '时序', 'local history', 'samples'],
    },
    {
        id: 'board', group: 'govern', section: 'control',
        entry: { kind: 'route', to: '/board' }, icon: 'Activity',
        keywords: ['总控', '深渊', '系统总控', 'board', 'god console'],
        access: { requires: ['topology'] },
    },
    {
        id: 'fleet', group: 'govern', section: 'control',
        entry: { kind: 'route', to: '/fleet' }, icon: 'Cpu',
        keywords: ['算力', '蜂群', '集群', 'fleet'],
        access: { requires: ['topology'] },
    },
    {
        id: 'edge', group: 'govern', section: 'control',
        entry: { kind: 'route', to: '/edge-console' }, icon: 'Server',
        keywords: ['边缘', 'edge'],
        access: { requires: ['topology'] },
    },
    {
        id: 'mining', group: 'govern', section: 'control',
        entry: { kind: 'route', to: '/mining' }, icon: 'Pickaxe',
        keywords: ['矿场', '算力', 'mining'],
        access: { requires: ['workflow'] },
    },
    {
        id: 'singularity', group: 'govern', section: 'control',
        entry: { kind: 'route', to: '/singularity' }, icon: 'Fingerprint',
        keywords: ['奇点', 'singularity'],
        access: { requires: ['workflow'] },
    },
    {
        id: 'gate', group: 'govern', section: 'security',
        entry: { kind: 'route', to: '/security/gate' }, icon: 'Lock',
        keywords: ['网关', '门禁', '零信', '沙箱', 'gate'],
        access: { requires: ['auditor'] },
    },
    {
        id: 'audit', group: 'govern', section: 'security',
        entry: { kind: 'route', to: '/phantom' }, icon: 'Eye',
        keywords: ['审计', '幽灵', '幻影', 'phantom'],
        access: { requires: ['auditor'] },
    },
    {
        id: 'vault', group: 'govern', section: 'security',
        entry: { kind: 'route', to: '/quantum-vault' }, icon: 'KeyRound',
        keywords: ['金库', '量子', '密钥', 'vault'],
        access: { requiresRole: 'Matrix_Architect' },
    },
    {
        id: 'rbac', group: 'govern', section: 'security',
        entry: { kind: 'route', to: '/rbac' }, icon: 'UserCog',
        keywords: ['角色', '权限', 'RBAC', 'rbac'],
        access: { requires: ['admin'] },
    },
    {
        id: 'remote_desktop', group: 'govern', section: 'security',
        entry: { kind: 'route', to: '/remote-desktop' }, icon: 'MonitorSmartphone',
        keywords: ['远程', '远控', 'remote', 'desktop', 'control', 'リモート', 'удалённый'],
        // [2026-10-03 对齐] 这里原有 `access: { requires: ['admin'] }`。批次13
        // 把路由门降成 `requiresAuth: true`（理由见 `router.ts` 该行注释）后，
        // 本镜像没跟着改 ⇒ 三方对账（`moduleRegistry.spec` ③）红成
        // "「放行的人拿到灰锁」正是这个形态"。`access` 只是**镜像语义**（不执法），
        // 所以修法是跟着执法面走：不再登记能力域。
    },
    {
        id: 'dev', group: 'govern', section: 'dev',
        entry: { kind: 'route', to: '/dev-workbench' }, icon: 'Wrench',
        keywords: ['开发者', '工作台', 'dev workbench'],
    },
    {
        id: 'design', group: 'govern', section: 'dev',
        entry: { kind: 'route', to: '/design-system' }, icon: 'Layers',
        keywords: ['设计系统', '令牌', 'design system'],
    },
    {
        id: 'import_center', group: 'govern', section: 'dev',
        entry: { kind: 'route', to: '/import-center' }, icon: 'HardDriveDownload',
        keywords: ['导入', '迁移', '会话历史', 'import', 'migration'],
    },

    // ═══ 系统/个人 tools（底带身份卡直达 / 面板可达；不上五组）═══
    {
        id: 'settings', group: 'tools', section: 'system',
        entry: { kind: 'route', to: '/settings' }, icon: 'Settings',
        keywords: ['设置', '系统设置', 'settings', '星环'],
    },
    {
        id: 'identity', group: 'tools', section: 'system',
        entry: { kind: 'route', to: '/user/identity' }, icon: 'Contact',
        keywords: ['身份', '图谱', '身份中心', 'identity'],
    },

    // ═══ 行业垂直 vertical（不上轨；面板/启动台可达）═══
    {
        id: 'medical', group: 'vertical', stage: 'vertical',
        entry: { kind: 'route', to: '/medical' }, icon: 'HeartPulse',
        keywords: ['医疗', '智慧医疗', 'medical'],
    },

    // ═══ beta / hidden（登记但不常驻；hidden 仅搜索命中时出现）═══
    {
        id: 'gateway', group: 'vertical', stage: 'beta',
        entry: { kind: 'route', to: '/gateway' }, icon: 'Network',
        keywords: ['网关', '神经网关', 'gateway'],
        access: { requiresOp: true, planned: true },
    },
    {
        id: 'orb', group: 'vertical', stage: 'beta',
        entry: { kind: 'route', to: '/orb' }, icon: 'CircleDot',
        keywords: ['orb', '球体管理'],
        access: { requires: ['topology'] },
    },
    {
        id: 'risk_bypass', group: 'tools', section: 'system', stage: 'hidden',
        entry: { kind: 'route', to: '/risk-bypass' }, icon: 'ShieldAlert',
        keywords: ['越权', '风险', 'risk bypass'],
        access: { requires: ['admin'] },
    },
    {
        id: 'telemetry_admin', group: 'tools', section: 'system', stage: 'hidden',
        entry: { kind: 'route', to: '/telemetry-admin' }, icon: 'LineChart',
        keywords: ['遥测管理', 'telemetry admin'],
        access: { requires: ['admin'] },
    },

    // ═══ 全息与实验子面（Q3 裁决：hidden —— 注册表全收，面板搜索可达）═══
    {
        id: 'hologram', group: 'tools', section: 'holo', stage: 'hidden',
        entry: { kind: 'route', to: '/hologram' }, icon: 'Sparkles',
        keywords: ['全息', 'hologram', '舞台'],
    },
    {
        id: 'visualizer', group: 'tools', section: 'holo', stage: 'hidden',
        entry: { kind: 'route', to: '/visualizer' }, icon: 'Eye',
        keywords: ['可视化', 'visualizer', '神经'],
    },
    {
        id: 'holo_hall', group: 'tools', section: 'holo', stage: 'hidden',
        entry: { kind: 'route', to: '/party' }, icon: 'Users',
        keywords: ['大厅', 'party', 'holo hall'],
    },
    {
        id: 'synapse', group: 'tools', section: 'holo', stage: 'hidden',
        entry: { kind: 'route', to: '/editor' }, icon: 'Workflow',
        keywords: ['突触', 'synapse', 'editor'],
    },
    {
        id: 'divination', group: 'tools', section: 'holo', stage: 'hidden',
        entry: { kind: 'route', to: '/divination' }, icon: 'Orbit',
        keywords: ['占卜', 'divination'],
    },
    {
        id: 'detached', group: 'tools', section: 'holo', stage: 'hidden',
        entry: { kind: 'route', to: '/detached' }, icon: 'Server',
        keywords: ['独立节点', 'detached'],
    },
];

// ── 派生视图（消费面只许派生，不许自登记）──────────────────────────────────

export interface DockItemView {
    id: string;
    to?: string;
    action?: string;
    icon: string;
    labelKey: string;
    descKey: string;
    stage?: ModuleStage;
    access?: ModuleAccess;
    /** 搜索别名（旧名/退役前缀词/缩写）——只进面板搜索，不进任何标签。 */
    keywords?: string[];
}

export interface DockSectionView {
    id: string;
    labelKey?: string;
    items: DockItemView[];
}

export interface DockGroupView {
    id: GroupId;
    icon: string;
    order: number;
    labelKey: string;
    descKey: string;
    theme?: 'gold';
    sections: DockSectionView[];
}

const labelKeyOf = (m: ModuleDescriptor) => `module.${m.id}.name`;
const descKeyOf = (m: ModuleDescriptor) => `module.${m.id}.desc`;

const toItemView = (m: ModuleDescriptor): DockItemView => ({
    id: m.id,
    ...(m.entry.kind === 'route' ? { to: m.entry.to } : { action: m.entry.action }),
    icon: m.icon,
    labelKey: labelKeyOf(m),
    descKey: descKeyOf(m),
    stage: m.stage,
    access: m.access,
    keywords: m.keywords,
});

const RAIL_GROUP_IDS: readonly GroupId[] = ['converse', 'create', 'operate', 'data', 'govern'];
const PALETTE_GROUP_IDS: readonly GroupId[] = [...RAIL_GROUP_IDS, 'vertical', 'tools'];

// 派生缓存：MODULES/组表是模块级常量，同参数的构建结果恒等 —— 轨/面板/启动台
// 多个 computed 反复取时不再重建数组。**消费方约定只读**（映射成新对象），不修改。
const groupViewCache = new Map<string, DockGroupView[]>();
function cachedGroups(key: string, ids: readonly GroupId[]): DockGroupView[] {
    let v = groupViewCache.get(key);
    if (!v) { v = buildGroups(ids); groupViewCache.set(key, v); }
    return v;
}

function buildGroups(ids: readonly GroupId[]): DockGroupView[] {
    return MODULE_GROUPS
        .filter(g => ids.includes(g.id))
        .map(g => ({
            id: g.id,
            icon: g.icon,
            order: g.order,
            labelKey: g.labelKey,
            descKey: g.descKey,
            theme: g.theme,
            sections: g.sections.map(s => ({
                id: s.id,
                labelKey: s.labelKey,
                // 注册顺序即呈现顺序：声明即顺序，不另行排序。
                items: MODULES.filter(m => m.group === g.id && (m.section ?? 'items') === s.id)
                    .map(toItemView),
            })).filter(s => s.items.length > 0 || g.id === 'pinned'),
        }));
}

/** 轨上五组（常用聚合组由消费方动态合成，不在内）。结果缓存（只读）。 */
export function dockGroups(): DockGroupView[] {
    return cachedGroups('rail', RAIL_GROUP_IDS);
}

/** 面板全集：五组 + 行业垂直 + 系统/个人（hidden 模块含在内，由 stage 决定呈现）。结果缓存（只读）。 */
export function paletteGroups(): DockGroupView[] {
    return cachedGroups('palette', PALETTE_GROUP_IDS);
}

export function moduleById(id: string): ModuleDescriptor | undefined {
    return MODULES.find(m => m.id === id);
}

/** 路径 → 名键（含 query 的路径原样作键，如 `/factory?mode=lingxi`）。 */
export function routeLabelPairs(): Array<{ path: string; labelKey: string; id: string }> {
    // flatMap + 字面量 kind 判别：filter 链不缩窄联合类型（TS2339 的成因）。
    return MODULES.flatMap(m =>
        m.entry.kind === 'route' ? [{ path: m.entry.to, labelKey: labelKeyOf(m), id: m.id }] : [],
    );
}

/** 与旧 `NAV_LABEL_KEYS` 同形的派生映射（路径 → i18n 键），兼容层用。 */
export function navLabelMap(): Record<string, string> {
    return Object.fromEntries(routeLabelPairs().map(p => [p.path, p.labelKey]));
}

/** 模块名键（按路径查；找不到返回 undefined）。 */
export function moduleLabelKeyForPath(path: string): string | undefined {
    return navLabelMap()[path];
}

/**
 * 路径归一（剥 query）——「模块在哪」的判定用，与「子项是否当前所在」的
 * `dockNavActive`（对 query 更严格）是两件事。
 */
export function normalizeModulePath(p: string): string {
    return p.includes('?') ? p.slice(0, p.indexOf('?')) : p;
}

/** 披露阈值（判据 6 的口径来源）：每节 ≤6 项、每组 ≤4 节、快捷动作 ≤2。 */
export const DISCLOSURE_LIMITS = Object.freeze({
    itemsPerSection: 6,
    sectionsPerGroup: 4,
    quickActionsPerGroup: 2,
    pinnedMax: 8,
});
